<script setup lang="ts">
import { deleteIcon, error, refresh } from '@/components/icons/icons';
import { close } from '@/components/icons/icons.ts';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { computed } from 'vue';
import type { FormMode } from '@/components/forms/types';
import { iconMapper } from '../utils';
import { isPredefinedEntity } from '@/utils/helper';
import { UploadProgress } from '../../../../utils/useFileUpload';
import { FileMetaInfoDto } from '@/api/generator';

export interface FileItemProps {
  file: FileMetaInfoDto;
  mode: FormMode;
  uploadProgress?: UploadProgress;
}

const props = defineProps<FileItemProps>();

const emit = defineEmits<{
  download: [FileMetaInfoDto];
  remove: [FileMetaInfoDto];
  retry: [FileMetaInfoDto];
}>();

const inStateFailed = computed(() => props.uploadProgress?.state === 'failed');

const isReady = computed(() => !props.uploadProgress || props.uploadProgress.state === 'success');

const isEllipsisActive = (elementId: string) => {
  const element = document.getElementById(elementId);

  if (element && element.parentElement) {
    return element.parentElement.offsetWidth < element.offsetWidth + 25;
  }
};

const icon = computed(() => (inStateFailed.value ? error : iconMapper(props.file.type)));
const isPredefined = computed(() => isPredefinedEntity(props.file));
</script>

<template>
  <v-hover>
    <template #default="{ isHovering, props: hoverProps }">
      <v-list-item
        v-bind="hoverProps"
        data-testid="fileListItem"
        class="pa-0 file-item text-body-2"
        density="compact"
        :state="uploadProgress?.state"
        @click="isReady && emit('download', file)"
      >
        <template #prepend>
          <v-icon size="24" :icon="icon" :color="inStateFailed ? 'error' : 'secondary'"></v-icon>
        </template>

        <Tooltip :disabled="!isEllipsisActive(file.id)">
          <template #target="{ props: tooltipProps }">
            <v-list-item-title
              v-bind="tooltipProps"
              data-testid="fileListItem_title"
              class="text-body-2 font-weight-medium"
            >
              <span :id="file.id" :class="[inStateFailed ? 'text-error' : 'secondary']">
                {{ file.name }}
              </span>

              <v-progress-linear
                v-if="uploadProgress?.state === 'pending'"
                data-testid="fileListItem_progress"
                location="top"
                height="3"
                :max="file.size"
                :modelValue="uploadProgress?.bytesUploaded"
              ></v-progress-linear>
            </v-list-item-title>
          </template>
          <template #tooltip>
            {{ file.name }}
          </template>
        </Tooltip>
        <template v-if="mode !== 'details'" #append>
          <span class="align-end d-flex flex-row">
            <Tooltip>
              <template #target="{ props: tooltipProps }">
                <TextVBtn
                  v-if="inStateFailed"
                  data-testid="fileListItem_retry"
                  color="secondary"
                  :icon="refresh"
                  v-bind="tooltipProps"
                  class="delete-btn"
                  size="small"
                  @click.stop="emit('retry', file)"
                >
                </TextVBtn>
              </template>
              <template #tooltip> {{ $t('RetryUpload') }} </template>
            </Tooltip>

            <Tooltip :disabled="false">
              <template #target="{ props: tooltipProps }">
                <div v-bind="tooltipProps">
                  <TextVBtn
                    v-if="(isReady && isHovering) || inStateFailed"
                    data-testid="fileListItem_delete"
                    color="secondary"
                    :icon="deleteIcon"
                    class="delete-btn"
                    size="small"
                    :disabled="isPredefined"
                    @click.stop="emit('remove', file)"
                  >
                  </TextVBtn>
                </div>
              </template>
              <template #tooltip>
                {{
                  isPredefined
                    ? $t('Exceptions.0NotAllowedToDeletePredefined1', {
                        name: file.name,
                        reason: $t('Attachment_one'),
                      })
                    : $t('DeleteAttachment')
                }}
              </template>
            </Tooltip>
            <Tooltip>
              <template #target="{ props: tooltipProps }">
                <TextVBtn
                  v-if="uploadProgress?.state === 'pending'"
                  data-testid="fileListItem_cancel"
                  :icon="close"
                  color="secondary"
                  class="delete-btn"
                  v-bind="tooltipProps"
                  size="small"
                  @click.stop="emit('remove', file)"
                >
                </TextVBtn>
              </template>
              <template #tooltip> {{ $t('CancelUpload') }} </template>
            </Tooltip>
          </span>
        </template>
      </v-list-item>
    </template>
  </v-hover>
</template>

<style scoped lang="scss">
.file-item {
  min-height: 28px;

  &.v-list-item--density-compact {
    &.v-list-item--one-line {
      min-height: 32px !important;
    }
  }

  &:hover {
    .delete-btn {
      display: inline;
      background: transparent;
    }

    ::v-deep(.v-list-item__append) {
      background: rgb(var(--v-theme-surface));
      position: relative;
    }

    ::v-deep(.v-list-item__overlay) {
      opacity: calc(0.15 * var(--v-theme-overlay-multiplier));
      transition: none !important;
    }
  }

  ::v-deep(.v-list-item__overlay) {
    transition: none !important;
  }

  .file-action {
    margin: 0;
    flex-direction: row;
    align-items: center;
  }
}

.v-list-item--density-default:not(.v-list-item--nav).v-list-item--one-line {
  padding-inline-start: 3px;
  padding-inline-end: 16px;
}

.delete-btn {
  display: inline;
  background: transparent;
}

.progress-text {
  font-size: 8px;
}
</style>
